import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { API } from "@/App";
import { toast } from "sonner";
import { safeRedirect } from "@/utils/api";
import BottomNav from "@/components/BottomNav";
import NotificationSettings from "@/components/NotificationSettings";
import {
  Crown, CreditCard, Shield, Download, Trash2,
  ChevronRight, LogOut, Check, Loader2,
  Gift, Users, Copy, Share2, Trophy, Moon, Sun, Link, Plane
} from "lucide-react";

export default function Settings() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [referralData, setReferralData] = useState(null);
  const [referralCode, setReferralCode] = useState("");
  const [isDarkMode, setIsDarkMode] = useState(() => localStorage.getItem("savecation_theme") === "dark");
  
  useEffect(() => {
    fetchUser();
    fetchReferralData();
  }, []);
  
  useEffect(() => {
    checkPaymentStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  
  const fetchUser = async () => {
    try {
      const response = await fetch(`${API}/auth/me`, { credentials: "include" });
      if (response.ok) setUser(await response.json());
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to load settings. Please refresh.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchReferralData = async () => {
    try {
      const [codeRes, statsRes] = await Promise.all([
        fetch(`${API}/referral/code`, { credentials: "include" }),
        fetch(`${API}/referral/stats`, { credentials: "include" })
      ]);
      if (codeRes.ok && statsRes.ok) {
        const code = await codeRes.json();
        const stats = await statsRes.json();
        setReferralData({ ...code, ...stats });
      }
    } catch (error) {
      console.error("Referral error:", error);
    }
  };
  
  const checkPaymentStatus = async () => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) return;
    
    setIsProcessing(true);
    
    try {
      const response = await fetch(`${API}/payments/status/${sessionId}`, {
        credentials: "include"
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.payment_status === "paid") {
          toast.success("Welcome to Pro! 🎉");
          navigate("/settings", { replace: true });
          fetchUser();
        }
      }
    } catch (error) {
      console.error("Status check error:", error);
    } finally {
      setIsProcessing(false);
    }
  };
  
  const handleApplyReferralCode = async () => {
    if (!referralCode.trim()) {
      toast.error("Please enter a referral code");
      return;
    }
    
    try {
      const response = await fetch(`${API}/referral/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ referral_code: referralCode.trim() })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message);
        setReferralCode("");
        fetchUser();
        fetchReferralData();
      } else {
        toast.error(data.detail || "Invalid referral code");
      }
    } catch (error) {
      toast.error("Failed to apply referral code");
    }
  };
  
  const copyReferralCode = () => {
    if (referralData?.referral_code) {
      navigator.clipboard.writeText(referralData.referral_code);
      toast.success("Referral code copied!");
    }
  };
  
  const copyReferralLink = () => {
    if (referralData?.referral_code) {
      const link = `${window.location.origin}/?ref=${referralData.referral_code}`;
      navigator.clipboard.writeText(link);
      toast.success("Referral link copied!");
    }
  };
  
  const shareReferral = () => {
    const code = referralData?.referral_code;
    if (!code) return;
    const link = `${window.location.origin}/?ref=${code}`;
    if (navigator.share) {
      navigator.share({
        title: "Join Savecation!",
        text: `Use my referral code ${code} to get $5 free credit! Save for your dream trips.`,
        url: link
      });
    } else {
      copyReferralLink();
    }
  };

  const toggleDarkMode = (enabled) => {
    setIsDarkMode(enabled);
    const theme = enabled ? "dark" : "light";
    localStorage.setItem("savecation_theme", theme);
    document.documentElement.setAttribute("data-theme", theme);
    toast.success(enabled ? "Night Flight mode activated" : "Switched to light mode");
  };
  
  const handleUpgrade = async (plan) => {
    setIsProcessing(true);
    try {
      const response = await fetch(`${API}/payments/subscription`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          plan,
          origin_url: window.location.origin
        })
      });
      
      if (!response.ok) throw new Error("Failed to create checkout");
      
      const data = await response.json();
      safeRedirect(data.url);
    } catch (error) {
      toast.error(error.message);
      setIsProcessing(false);
    }
  };
  
  const handleExportData = async (format = "json") => {
    try {
      const response = await fetch(`${API}/user/export`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to export data");
      const data = await response.json();

      if (format === "csv") {
        // Flatten savings history to CSV
        const rows = (data.savings_history || []).map(e =>
          [e.created_at, e.amount, e.note || "", e.goal_id || ""].join(",")
        );
        const csv = ["date,amount,note,goal_id", ...rows].join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "savecation-savings.csv"; a.click();
        URL.revokeObjectURL(url);
      } else if (format === "pdf") {
        // Simple HTML-to-print approach
        const goals = (data.goals || []).map(g =>
          `<tr><td>${g.title}</td><td>$${g.current_amount?.toFixed(2)}</td><td>$${g.target_amount?.toFixed(2)}</td><td>${g.status}</td></tr>`
        ).join("");
        const html = `<html><body style="font-family:sans-serif;padding:24px">
          <h1>Savecation Data Export</h1>
          <p>Exported: ${new Date().toLocaleDateString()}</p>
          <h2>Goals</h2>
          <table border="1" cellpadding="6" style="border-collapse:collapse;width:100%">
            <tr><th>Title</th><th>Saved</th><th>Target</th><th>Status</th></tr>${goals}
          </table>
          <h2>Total Saved: $${data.summary?.total_saved?.toFixed(2) || "0.00"}</h2>
        </body></html>`;
        const w = window.open("", "_blank");
        w.document.write(html);
        w.document.close();
        w.print();
      } else {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "savecation-data-export.json"; a.click();
        URL.revokeObjectURL(url);
      }
      toast.success("Data exported successfully");
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm("Cancel your subscription and downgrade to the free plan?")) return;
    try {
      const response = await fetch(`${API}/payments/cancel-subscription`, {
        method: "POST",
        credentials: "include"
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail);
      toast.success(data.message);
      fetchUser();
    } catch (error) {
      toast.error(error.message);
    }
  };
  
  const handleDeleteAccount = async () => {
    try {
      const response = await fetch(`${API}/user/account`, {
        method: "DELETE",
        credentials: "include"
      });
      
      if (!response.ok) throw new Error("Failed to delete account");
      
      toast.success("Account deleted. Goodbye! 👋");
      navigate("/");
    } catch (error) {
      toast.error(error.message);
    }
  };
  
  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include"
      });
      navigate("/");
    } catch (error) {
      navigate("/");
    }
  };
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 safe-bottom" data-testid="settings-skeleton">
        <div className="bg-white border-b border-slate-100 px-6 py-4">
          <div className="h-6 w-24 bg-slate-200 rounded animate-pulse mb-1" />
          <div className="h-3 w-36 bg-slate-100 rounded animate-pulse" />
        </div>
        <div className="px-6 py-6 space-y-4">
          {/* Profile card skeleton */}
          <div className="bg-white rounded-2xl p-5 shadow-sm flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-slate-200 animate-pulse" />
            <div className="flex-1">
              <div className="h-5 w-32 bg-slate-200 rounded animate-pulse mb-2" />
              <div className="h-3 w-44 bg-slate-100 rounded animate-pulse" />
            </div>
          </div>
          {/* Settings rows skeleton */}
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="bg-white rounded-2xl p-4 shadow-sm flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-slate-200 animate-pulse" />
                <div className="h-4 w-32 bg-slate-200 rounded animate-pulse" />
              </div>
              <div className="w-5 h-5 bg-slate-100 rounded animate-pulse" />
            </div>
          ))}
        </div>
        <BottomNav />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-50 safe-bottom" data-testid="settings-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 px-6 py-4">
        <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
      </header>
      
      <main className="px-6 py-6">
        {/* Profile Section */}
        <section className="savecation-card mb-6">
          <div className="flex items-center gap-4 mb-4">
            <img 
              src={user?.picture || "https://via.placeholder.com/80"}
              alt={user?.name}
              className="w-16 h-16 rounded-full object-cover"
            />
            <div className="flex-1">
              <h2 className="text-xl font-bold text-slate-900">{user?.name}</h2>
              <p className="text-sm text-slate-500">{user?.email}</p>
            </div>
          </div>
          
          {/* Subscription Badge */}
          <div className={`flex items-center justify-between p-4 rounded-2xl ${
            user?.subscription_tier === "pro" || user?.subscription_tier === "team"
              ? "bg-gradient-to-r from-amber-50 to-amber-100 border border-amber-200"
              : "bg-slate-50 border border-slate-100"
          }`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                user?.subscription_tier !== "free" ? "bg-amber-200 text-amber-700" : "bg-slate-200 text-slate-600"
              }`}>
                {user?.subscription_tier === "team" ? <Users className="w-5 h-5" /> : <Crown className="w-5 h-5" />}
              </div>
              <div>
                <p className="font-semibold text-slate-900">
                  {user?.subscription_tier === "team" ? "Team Member" : 
                   user?.subscription_tier === "pro" ? "Pro Member" : "Free Plan"}
                </p>
                <p className="text-xs text-slate-500">
                  {user?.subscription_tier === "team" 
                    ? "Team goals & shared savings"
                    : user?.subscription_tier === "pro" 
                    ? "Unlimited goals & AI requests"
                    : "3 goals, 5 AI requests/hour"}
                </p>
              </div>
            </div>
            {user?.subscription_tier === "free" ? (
              <Button
                onClick={() => setShowUpgradeModal(true)}
                size="sm"
                className="bg-amber-500 hover:bg-amber-600 rounded-full font-bold"
                data-testid="upgrade-btn"
              >
                Upgrade
              </Button>
            ) : (
              <Button
                onClick={handleCancelSubscription}
                size="sm"
                variant="outline"
                className="rounded-full text-rose-500 border-rose-200 hover:bg-rose-50 text-xs"
                data-testid="cancel-subscription-btn"
              >
                Cancel Plan
              </Button>
            )}
          </div>
        </section>
        
        {/* Referral Section */}
        <section className="savecation-card mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
              <Gift className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-slate-900">Refer Friends, Earn $10</p>
              <p className="text-xs text-slate-500">Share your code & earn when friends join</p>
            </div>
          </div>
          
          {referralData?.referral_code && (
            <>
              <div className="flex items-center gap-2 mb-3">
                <div className="flex-1 bg-slate-100 rounded-xl px-4 py-3 font-mono font-bold text-lg text-center tracking-wider">
                  {referralData.referral_code}
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={copyReferralCode}
                  className="h-12 w-12 rounded-xl"
                  data-testid="copy-referral-code"
                >
                  <Copy className="w-5 h-5" />
                </Button>
                <Button
                  onClick={shareReferral}
                  className="h-12 px-4 rounded-xl bg-emerald-500 hover:bg-emerald-600"
                  data-testid="share-referral-btn"
                >
                  <Share2 className="w-5 h-5" />
                </Button>
              </div>
              <Button
                variant="outline"
                onClick={copyReferralLink}
                className="w-full rounded-xl mb-4 text-sm"
                data-testid="copy-referral-link"
              >
                <Link className="w-4 h-4 mr-2" /> Copy Invite Link
              </Button>
            </>
          )}
          
          <div className="grid grid-cols-2 gap-4 text-center">
            <div className="bg-slate-50 rounded-xl p-3">
              <p className="text-2xl font-bold text-emerald-600">{referralData?.total_referrals || 0}</p>
              <p className="text-xs text-slate-500">Friends Referred</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-3">
              <p className="text-2xl font-bold text-emerald-600">${referralData?.total_earnings?.toFixed(2) || "0.00"}</p>
              <p className="text-xs text-slate-500">Earnings</p>
            </div>
          </div>
          
          <Button 
            variant="outline"
            onClick={() => setShowReferralModal(true)}
            className="w-full mt-4 rounded-full"
          >
            <Trophy className="w-4 h-4 mr-2" /> View Leaderboard
          </Button>
        </section>
        
        {/* Notification Settings */}
        <section className="mb-6">
          <p className="text-sm font-semibold text-slate-500 mb-3 uppercase tracking-wide">Notifications</p>
          <NotificationSettings />
        </section>
        
        {/* Home Airport */}
        <section className="mb-6">
          <p className="text-sm font-semibold text-slate-500 mb-3 uppercase tracking-wide">Travel Preferences</p>
          <div className="p-4 bg-white rounded-2xl border border-slate-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center">
                <Plane className="w-5 h-5 text-sky-600" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-slate-900">Home Airport</p>
                <p className="text-xs text-slate-500">Used for "Deals Near You" recommendations</p>
              </div>
            </div>
            <div className="relative">
              <Input
                list="iata-airports"
                placeholder="e.g., JFK, LHR, CDG"
                defaultValue={user?.home_airport || ""}
                onBlur={async (e) => {
                  const code = e.target.value.toUpperCase().trim();
                  if (!code) return;
                  try {
                    const res = await fetch(`${API}/auth/airport`, {
                      method: "PUT",
                      headers: { "Content-Type": "application/json" },
                      credentials: "include",
                      body: JSON.stringify({ airport_code: code })
                    });
                    if (res.ok) {
                      setUser(prev => ({ ...prev, home_airport: code }));
                      toast.success(`Home airport set to ${code}`);
                    }
                  } catch { toast.error("Failed to update"); }
                }}
                className="savecation-input uppercase"
                data-testid="home-airport-input"
              />
              <datalist id="iata-airports">
                {[
                  "JFK - New York JFK", "LAX - Los Angeles", "ORD - Chicago O'Hare",
                  "MIA - Miami", "SFO - San Francisco", "ATL - Atlanta", "DFW - Dallas",
                  "DEN - Denver", "SEA - Seattle", "BOS - Boston", "IAH - Houston",
                  "LHR - London Heathrow", "CDG - Paris Charles de Gaulle", "DXB - Dubai",
                  "NRT - Tokyo Narita", "SYD - Sydney", "AMS - Amsterdam", "FRA - Frankfurt",
                  "SIN - Singapore Changi", "HKG - Hong Kong", "ICN - Seoul Incheon",
                  "YYZ - Toronto Pearson", "MEX - Mexico City", "GRU - São Paulo",
                  "CPT - Cape Town", "JNB - Johannesburg", "CAI - Cairo", "BOM - Mumbai",
                  "DEL - New Delhi", "BKK - Bangkok", "KUL - Kuala Lumpur",
                ].map(ap => <option key={ap} value={ap.split(" - ")[0]}>{ap}</option>)}
              </datalist>
            </div>
          </div>
        </section>

        {/* Appearance */}
        <section className="mb-6">
          <p className="text-sm font-semibold text-slate-500 mb-3 uppercase tracking-wide">Appearance</p>
          <div className="flex items-center justify-between p-4 bg-white rounded-2xl border border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                {isDarkMode ? <Moon className="w-5 h-5 text-indigo-600" /> : <Sun className="w-5 h-5 text-amber-500" />}
              </div>
              <div>
                <p className="font-semibold text-slate-900">Night Flight Mode</p>
                <p className="text-xs text-slate-500">{isDarkMode ? "Dark theme active" : "Light theme active"}</p>
              </div>
            </div>
            <Switch
              checked={isDarkMode}
              onCheckedChange={toggleDarkMode}
              data-testid="dark-mode-toggle"
            />
          </div>
        </section>
        
        {/* Menu Items */}
        <section className="space-y-3 mb-6">
          <MenuItem 
            icon={CreditCard}
            label="Wallet & Payments"
            onClick={() => navigate("/wallet")}
            data-testid="wallet-settings"
          />
          <MenuItem 
            icon={Shield}
            label="Privacy & Security"
            sublabel="Manage your data"
          />
          <div className="bg-white rounded-2xl border border-slate-100 p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                <Download className="w-5 h-5 text-slate-600" />
              </div>
              <p className="font-semibold text-slate-900">Export My Data</p>
            </div>
            <div className="flex gap-2">
              {["json","csv","pdf"].map(fmt => (
                <Button
                  key={fmt}
                  variant="outline"
                  size="sm"
                  className="flex-1 rounded-xl text-xs font-bold uppercase"
                  onClick={() => handleExportData(fmt)}
                  data-testid={`export-${fmt}`}
                >
                  {fmt}
                </Button>
              ))}
            </div>
          </div>
        </section>
        
        {/* Danger Zone */}
        <section className="mb-24">
          <p className="text-sm font-semibold text-slate-500 mb-3 uppercase tracking-wide">Danger Zone</p>
          <div className="space-y-3">
            <MenuItem 
              icon={Trash2}
              label="Delete Account"
              sublabel="Permanently remove all data"
              danger
              onClick={() => setShowDeleteModal(true)}
              data-testid="delete-account"
            />
            <MenuItem 
              icon={LogOut}
              label="Log Out"
              onClick={handleLogout}
              data-testid="logout-btn"
            />
          </div>
        </section>
      </main>
      
      {/* Upgrade Modal */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className="sm:max-w-lg rounded-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Crown className="w-6 h-6 text-amber-500" /> Choose Your Plan
            </DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="pro" className="mt-4">
            <TabsList className="grid w-full grid-cols-2 rounded-2xl bg-slate-100 p-1 h-12">
              <TabsTrigger value="pro" className="rounded-xl data-[state=active]:bg-white">
                <Crown className="w-4 h-4 mr-2" /> Pro
              </TabsTrigger>
              <TabsTrigger value="team" className="rounded-xl data-[state=active]:bg-white">
                <Users className="w-4 h-4 mr-2" /> Team
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="pro" className="mt-4 space-y-4">
              <div className="bg-gradient-to-r from-amber-50 to-amber-100 rounded-2xl p-5">
                <h3 className="font-bold text-slate-900 mb-3">Pro Benefits:</h3>
                <ul className="space-y-2 text-sm">
                  {[
                    "Unlimited savings goals",
                    "Group goals with friends & family",
                    "50 AI requests per hour",
                    "Priority customer support",
                    "Early access to new features"
                  ].map((benefit, i) => (
                    <li key={i} className="flex items-center gap-2 text-slate-700">
                      <Check className="w-4 h-4 text-amber-600 flex-shrink-0" /> {benefit}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <Button
                  onClick={() => handleUpgrade("pro_monthly")}
                  disabled={isProcessing}
                  variant="outline"
                  className="flex-col h-auto py-4 rounded-2xl"
                >
                  <span className="text-2xl font-bold">$9.99</span>
                  <span className="text-sm text-slate-500">Monthly</span>
                </Button>
                <Button
                  onClick={() => handleUpgrade("pro_yearly")}
                  disabled={isProcessing}
                  className="flex-col h-auto py-4 rounded-2xl bg-amber-500 hover:bg-amber-600"
                >
                  <span className="text-2xl font-bold">$39.99</span>
                  <span className="text-sm text-amber-100">Yearly (Save 33%)</span>
                </Button>
              </div>
            </TabsContent>
            
            <TabsContent value="team" className="mt-4 space-y-4">
              <div className="bg-gradient-to-r from-sky-50 to-sky-100 rounded-2xl p-5">
                <h3 className="font-bold text-slate-900 mb-3">Team Benefits:</h3>
                <ul className="space-y-2 text-sm">
                  {[
                    "Everything in Pro",
                    "Shared team wallet",
                    "Up to 5 or 10 members",
                    "Team goal tracking",
                    "Admin dashboard",
                    "Priority support"
                  ].map((benefit, i) => (
                    <li key={i} className="flex items-center gap-2 text-slate-700">
                      <Check className="w-4 h-4 text-sky-600 flex-shrink-0" /> {benefit}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="space-y-3">
                <p className="text-sm font-semibold text-slate-500">Team (5 members)</p>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={() => handleUpgrade("team_monthly")}
                    disabled={isProcessing}
                    variant="outline"
                    className="flex-col h-auto py-4 rounded-2xl"
                  >
                    <span className="text-xl font-bold">$9.99</span>
                    <span className="text-xs text-slate-500">Monthly</span>
                  </Button>
                  <Button
                    onClick={() => handleUpgrade("team_yearly")}
                    disabled={isProcessing}
                    className="flex-col h-auto py-4 rounded-2xl bg-sky-500 hover:bg-sky-600"
                  >
                    <span className="text-xl font-bold">$79.99</span>
                    <span className="text-xs text-sky-100">Yearly (Save 33%)</span>
                  </Button>
                </div>
                
                <p className="text-sm font-semibold text-slate-500 mt-4">Team+ (10 members)</p>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={() => handleUpgrade("team_plus_monthly")}
                    disabled={isProcessing}
                    variant="outline"
                    className="flex-col h-auto py-4 rounded-2xl"
                  >
                    <span className="text-xl font-bold">$19.99</span>
                    <span className="text-xs text-slate-500">Monthly</span>
                  </Button>
                  <Button
                    onClick={() => handleUpgrade("team_plus_yearly")}
                    disabled={isProcessing}
                    className="flex-col h-auto py-4 rounded-2xl bg-sky-500 hover:bg-sky-600"
                  >
                    <span className="text-xl font-bold">$149.99</span>
                    <span className="text-xs text-sky-100">Yearly (Save 38%)</span>
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
          
          {isProcessing && (
            <div className="flex items-center justify-center gap-2 text-slate-600 mt-4">
              <Loader2 className="w-5 h-5 animate-spin" />
              Redirecting to checkout...
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Referral Leaderboard Modal */}
      <Dialog open={showReferralModal} onOpenChange={setShowReferralModal}>
        <DialogContent className="sm:max-w-md rounded-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Trophy className="w-6 h-6 text-amber-500" /> Referral Leaderboard
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-emerald-50 rounded-2xl p-4 text-center">
              <p className="text-sm text-emerald-700 mb-1">Your Referral Code</p>
              <p className="text-2xl font-mono font-bold tracking-widest" data-testid="referral-code-display">{referralData?.referral_code}</p>
            </div>
            
            {/* Apply Code Section */}
            {!user?.referred_by && (
              <div className="space-y-2">
                <p className="text-sm font-semibold text-slate-500">Have a referral code?</p>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter code"
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                    className="savecation-input uppercase"
                    maxLength={8}
                    data-testid="apply-referral-input"
                  />
                  <Button onClick={handleApplyReferralCode} className="savecation-button" data-testid="apply-referral-btn">
                    Apply
                  </Button>
                </div>
              </div>
            )}
            
            <LeaderboardSection />
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="sm:max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-rose-600">Delete Account</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-slate-600">
              Are you sure you want to delete your account? This action cannot be undone. All your:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-1">
              <li>Savings goals</li>
              <li>Wallet balance</li>
              <li>Transaction history</li>
              <li>Community posts</li>
            </ul>
            <p className="text-slate-600">...will be permanently deleted.</p>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 rounded-full"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteAccount}
                className="flex-1 rounded-full bg-rose-500 hover:bg-rose-600"
                data-testid="confirm-delete"
              >
                Delete Forever
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      <BottomNav />
    </div>
  );
}

function MenuItem({ icon: Icon, label, sublabel, danger, onClick, ...props }) {
  return (
    <button 
      onClick={onClick}
      className={`w-full flex items-center justify-between p-4 bg-white rounded-2xl border border-slate-100 hover:border-slate-200 transition-colors ${
        danger ? "hover:border-rose-200 hover:bg-rose-50" : ""
      }`}
      {...props}
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
          danger ? "bg-rose-100 text-rose-600" : "bg-slate-100 text-slate-600"
        }`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="text-left">
          <p className={`font-semibold ${danger ? "text-rose-600" : "text-slate-900"}`}>{label}</p>
          {sublabel && <p className="text-xs text-slate-500">{sublabel}</p>}
        </div>
      </div>
      <ChevronRight className={`w-5 h-5 ${danger ? "text-rose-400" : "text-slate-400"}`} />
    </button>
  );
}

function LeaderboardSection() {
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const res = await fetch(`${API}/referral/leaderboard`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          setLeaders(data.leaderboard || []);
        }
      } catch (e) {
        console.error("Leaderboard error:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchLeaderboard();
  }, []);
  
  if (loading) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }
  
  if (leaders.length === 0) {
    return (
      <div className="text-center py-6 text-slate-500">
        <Trophy className="w-10 h-10 mx-auto mb-2 opacity-30" />
        <p className="font-medium">No referrers yet</p>
        <p className="text-sm">Be the first to refer a friend!</p>
      </div>
    );
  }
  
  const medals = ["bg-amber-400 text-white", "bg-slate-300 text-white", "bg-orange-400 text-white"];
  
  return (
    <div className="space-y-2" data-testid="leaderboard-list">
      <p className="text-sm font-semibold text-slate-500">Top Referrers</p>
      {leaders.map((leader, i) => (
        <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${medals[i] || "bg-slate-200 text-slate-600"}`}>
            {i + 1}
          </div>
          {leader.picture ? (
            <img src={leader.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center text-sky-600 font-bold text-sm">
              {leader.name?.charAt(0) || "?"}
            </div>
          )}
          <div className="flex-1">
            <p className="font-semibold text-slate-900 text-sm">{leader.name}</p>
          </div>
          <p className="font-bold text-emerald-600">${leader.referral_earnings?.toFixed(2)}</p>
        </div>
      ))}
    </div>
  );
}
